/* eslint-disable no-console */
import axios from 'axios'

import { sortByDate } from '../Static/sortUtils'

export const salesResources = {
  sales: 'sales',
  latestDownloads: 'latest_downloads',
  monthlySummary: 'monthly_summary',
  paymentsInfo: 'payments_info',
  discounts: 'discounts',
  coupons: 'coupons',
  donations: 'donations',
  followers: 'followers',
  referrals: 'referrals',
  subscriptionSales: 'subscription_sales',
  availableMonths: 'available_months',
}

export const mySalesService = {
  async getReferralLink() {
    const referralLink = await axios.get('/api/internal/profile/sales/referral_link')
    return referralLink
  },

  async getReferrals() {
    const referrals = await this.fetchResource(salesResources.referrals)
    return referrals
  },

  async getLatestSales({ page = 1, perPage, month } = {}) {
    const params = { page }
    if (perPage != null) params.per_page = perPage
    if (month != null) params.month = month
    return this.fetchResource(
      salesResources.sales,
      params,
      { includeMeta: true }
    )
  },

  async getSubscriptionSales({ page = 1, perPage, month } = {}) {
    const params = { page }
    if (perPage != null) params.per_page = perPage
    if (month != null) params.month = month
    return this.fetchResource(
      salesResources.subscriptionSales,
      params,
      { includeMeta: true }
    )
  },

  async getPaymentsInfo() {
    const paymentsInfo = await this.fetchResource(salesResources.paymentsInfo)
    return paymentsInfo
  },

  async getMonthlySummary({ monthsCount } = {}) {
    const params = monthsCount != null ? { months_count: monthsCount } : {}
    const monthlySummary = await this.fetchResource(
      salesResources.monthlySummary,
      params
    )
    return monthlySummary
  },

  async createCoupon(coupon) {
    const response = await axios.post('/profile/coupons', coupon)
    return response
  },

  async getCoupons() {
    const coupons = await this.fetchResource(salesResources.coupons)
    const activeCoupons = coupons.filter((coupon) => coupon.suspended !== true)
    return activeCoupons
  },

  async deleteCoupon(id) {
    const response = await axios.delete(`/profile/coupons/${id}`)
    return response
  },

  async getAvailableMonths() {
    const response = await axios.get('/api/internal/profile/sales', {
      params: { resource: salesResources.availableMonths },
    })
    return {
      sales: response.data?.sales || [],
      downloads: response.data?.downloads || [],
      subscriptionSales: response.data?.subscription_sales || [],
    }
  },

  async getLatestDownloads({ page = 1, perPage, month } = {}) {
    const params = { page }
    if (perPage != null) params.per_page = perPage
    if (month != null) params.month = month
    return this.fetchResource(
      salesResources.latestDownloads,
      params,
      { includeMeta: true }
    )
  },

  async getDiscounts() {
    const discounts = await this.fetchResource(salesResources.discounts)
    return discounts
  },

  async saveDiscounts(discountData) {
    const response = await axios.put(
      '/api/internal/profile/sales/update/discounts',
      {
        discounts: {
          discount: parseInt(discountData.regularDiscount, 10),
          super_discount: parseInt(discountData.superDiscount, 10),
          sale_off: discountData.participateAll,
          sell_for_equity: discountData.sellForEquity,
          sell_on_other_platforms: discountData.sellOnOtherPlatforms,
        },
      }
    )

    return response.data
  },

  async getDonations() {
    const donations = await this.fetchResource(salesResources.donations)
    return donations
  },

  async disableDonations() {
    const response = await axios.put('/profile/disable-donations')
    return response
  },

  async setDonationRate(donationRate) {
    const response = await axios.put('/profile/set-donation', {
      donation_rate: donationRate.toString(),
    })
    return response
  },

  async getFollowers() {
    const followers = await this.fetchResource(salesResources.followers)

    return sortByDate(followers, 'followSince', false)
  },

  async generateReport(period) {
    const response = await axios.get('/profile/reports/downloads', { params: period ? { period } : {} })
    return response.status
  },

  async generateSalesReport(period) {
    const response = await axios.get('/profile/reports/sales', { params: period ? { period } : {} })
    return response.status
  },

  async generateSubscriptionEarningsReport(period) {
    const response = await axios.get('/profile/reports/subscription_earnings', { params: period ? { period } : {} })
    return response.status
  },

  updateSaleOff(checked) {
    return axios.put('/profile/set_sale_off', { sale_off: checked })
  },

  updateDiscount(discount) {
    return axios.put('/profile/set_discount', { discount })
  },

  async confirmDonationRate(donationRate) {
    console.log('confirm donation rate', donationRate)
  },

  async getRefunds(refundIds) {
    const response = await axios.get('/profile/refunds', {
      params: {
        refund_ids: refundIds,
      },
    })
    return this.mapResponseToAttributes(response.data)
  },

  async performFollowAction(url, method) {
    if (method === 'post') {
      return axios.post(url)
    }
    if (method === 'delete') {
      return axios.delete(url)
    }
    throw new Error(`Unsupported method: ${method}`)
  },

  async fetchResource(resource, params = {}, options = {}) {
    const response = await axios.get(
      '/api/internal/profile/sales',
      {
        params: {
          resource,
          ...params,
        },
      }
    )
    const data = this.mapResponseToAttributes(response.data)

    if (options.includeMeta) {
      return {
        data,
        meta: response.data.meta || {},
      }
    }

    return data
  },

  mapResponseToAttributes(response) {
    if (!response || !response.data) {
      return []
    }

    if (!Array.isArray(response.data)) {
      const item = response.data
      const attributes = item.attributes || {}
      return {
        id: item.id,
        ...attributes,
      }
    }

    return response.data.map((item) => {
      const attributes = item.attributes || {}
      return {
        id: item.id,
        ...attributes,
      }
    })
  },
}
